<template>
  <RebookEmptyPage v-if="!enabled" />
  <div
    v-else
    :class="[
      $style.base,
      {
        [$style.smallScreen]: $screen === 's'
      }
    ]"
  >
    <router-view />
    <div :class="$style.header">
      <Flex direction="column">
        <Flex>
          <BaseToggle
            v-model="enabled"
            v-test="'intro-toggle'"
          />
          <BaseHeading>
            {{ $t('rebook_reminder.empty_state.disable') }}
          </BaseHeading>
        </Flex>
        <BaseText>
          {{ $t('marketing.email.automated_emails.rebook.receive_info') }}
        </BaseText>
      </Flex>
    </div>
    <div :class="$style.inner">
      <div :class="$style.wrap">
        <div :class="$style.col">
          <RebookStats dashboard />
        </div>
        <Flex
          direction="column"
          :class="$style.col"
        >
          <BaseCard
            :heading="filters.capitalize($t('global.items.service', 2))"
          >
            <template #header>
              <BaseLabel
                color="success"
                v-test="'enabled-services-count'"
              >
                {{ $t('rebook_reminder.services_selected', {
                  amount: rebookEnabledServicesCount
                }) }}
              </BaseLabel>
            </template>
            <Flex direction="column">
              <BaseText>
                {{ $t('rebook_reminder.description_services') }}
              </BaseText>
              <BaseButton
                fullWidth
                color="inverted"
                :routerLink="{
                name: 'rebook-reminder-services'
              }"
                v-test="'open-rebook-setup'"
              >
                {{ $t('rebook_reminder.modify_services') }}
              </BaseButton>
            </Flex>
          </BaseCard>
          <BaseCard :heading="$t('global.email')">
            <template #header>
              <BaseLabel
                color="success"
                state="enabled"
              />
            </template>
            <Flex direction="column">
              <BaseText>{{ $t('rebook_reminder.description_email') }}</BaseText>
              <BaseButton
                fullWidth
                color="inverted"
                :routerLink="{
                  name: 'rebook-reminder-template'
                }"
                @click="clickTemplate"
                v-test="'edit-rebook-email'"
              >
                {{ $t('rebook_reminder.customize_email') }}
              </BaseButton>
            </Flex>
          </BaseCard>
        </Flex>
      </div>
    </div>
  </div>
</template>

<script
  setup
  lang="ts"
>
import { storeToRefs } from 'pinia';
import { useI18n } from 'vue-i18n';
import filters from '@/filters';
import { flash, modal } from '@/helpers/ui';
import router from '@/router';
import { useCompanyStore } from '@/stores/company';
import { useServicesStore } from '@/stores/services';
import RebookStats from '../../insights/marketing-area/RebookStats.vue';
import { useMarketingBuilderStore } from '../../store/builder';
import { useContactEmailVerification } from '../../useContactEmailVerification';
import RebookEmptyPage from './RebookEmptyPage.vue';

const { updateCompany } = useCompanyStore();
const { company } = storeToRefs(useCompanyStore());
const { contactEmailVerified, verifyContactEmail } =
  useContactEmailVerification();
const { t } = useI18n();
const builderStore = useMarketingBuilderStore();
const { rebookEnabledServicesCount } = storeToRefs(useServicesStore());

const enabled = computed({
  get() {
    return company.value.settings.bookings.rebookReminderEnabled;
  },
  set() {
    const input = {
      settings: {
        bookings: {
          rebookReminderEnabled: false,
        },
      },
    };

    updateCompany(input).then(() => {
      builderStore.setEnabled(false);
      router.push({ name: 'rebook-reminder-dashboard' });
      flash(t('global.flash.rebook_reminder_disabled'));
    });
  },
});

const clickTemplate = () => {
  if (!contactEmailVerified) {
    modal('confirmation', {
      type: 'sendEmail',
      subMessage: t('email_templates.verification_modal.sub_message'),
      message: t('email_templates.verification_modal.message'),
    }).then(() => {
      verifyContactEmail();
    });
  }
};
</script>

<style
  lang="scss"
  module
>
.header {
  margin: $spacing * -1;
  padding: $spacing;
  background-color: var(--color-bg);
  border-bottom: 1px solid var(--color-border);
}

.inner {
  background: var(--color-bg-secondary);
  width: calc(100% + #{$spacing * 2});
  padding: $spacing * 1.5;
  margin-top: $spacing;
  margin-left: -$spacing;
}

.wrap {
  max-width: 900px;
  margin: 0 auto;
  display: flex;
  gap: $spacing * 1.5;

  .base.smallScreen & {
    flex-direction: column;
  }
}

.col {
  .base:not(.smallScreen) & {
    width: 50%;
  }
}
</style>
